import { ChangeDetectionStrategy, Component, inject, OnInit, viewChild } from '@angular/core';
import { FormArray, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import { AgGridThemeService } from '@sphere/components';
import { SphereJSONSchema, SphereJSONSchemaArray, SphereJSONSchemaObject, SphereJSONSchemaString } from '../formly-control-builder.models';
import { FormlyControlBuilderService } from '../formly-control-builder.service';
import { DateStringCellEditorComponent, IDateStringCellEditorParams } from './date-string-cell-editor.component';
import { FormArrayTableToolsCellRendererComponent, FormArrayTableToolsParams } from './form-array-table-tools-cell-renderer.component';
import { INumberCellEditorParams, NumberCellEditorComponent } from './number-cell-editor.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, RowDataUpdatedEvent } from 'ag-grid-community';
import { map, tap } from 'rxjs/operators';
import { NgTemplateOutlet } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

export type FormlyFormArrayFieldConfig = FormlyFieldConfig<
    FormlyFieldProps & {
        schema: SphereJSONSchemaArray;
    }
>;

@Component({
    templateUrl: './formly-form-array.component.html',
    styleUrls: ['./formly-form-array.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, FormlyModule, MatDivider, MatIconButton, MatIcon, MatButton, AgGridAngular]
})
export class FormlyFormArrayComponent extends FieldType<FormlyFormArrayFieldConfig> implements OnInit {
    private formlyBuilder = inject(FormlyControlBuilderService);
    private snackbar = inject(MatSnackBar);

    readonly grid = viewChild<AgGridAngular>('formArrayGrid');

    private agThemeService = inject(AgGridThemeService);
    comfortableTheme = this.agThemeService.comfortableTheme();

    public formGroup!: FormGroup<{ key: FormArray<UntypedFormGroup> }>;
    public fields: FormlyFieldConfig[][] = [];

    public gridOptions: GridOptions = {
        defaultColDef: {
            suppressMovable: true,
            suppressHeaderMenuButton: true
        }
    };
    public columnDefs: ColDef<any>[] = [];
    public rows: any[] = [];
    public pinnedTopRowData: any[] = [];

    public tableMode = true;
    ngOnInit() {
        this.formGroup = new FormGroup({
            key: new UntypedFormArray([])
        });

        if (this.tableMode) {
            this.columnDefs = this.initTableColumns(this.props.schema);
            this.rows = this.formControl.value.map((v: any, index: number) => ({ ...v, index }));
            this.pinnedTopRowData = [this.pinnedRow];
        } else {
            this.init(this.props.schema, this.formControl.value);
        }

        this.formGroup.valueChanges
            .pipe(
                map(formValue => formValue.key),
                tap(v => {
                    console.debug(v);
                    this.formControl.setValue(v);
                })
            )
            .subscribe();
    }

    public get formArray() {
        return this.formGroup.controls.key;
    }

    public removeItem(index: number) {
        this.formGroup.controls.key.removeAt(index);
        this.fields = this.fields.filter((_, i) => i !== index);
    }

    public addItem() {
        this.fields.push(this.fieldsForGroup(this.props.schema.items as SphereJSONSchemaObject) as any);
        this.formGroup.controls.key.push(this.getControlGroup());
    }

    private init(schema: SphereJSONSchemaArray, formState: any[]) {
        if (Array.isArray(formState)) {
            for (const itemState of formState) {
                this.fields.push(this.fieldsForGroup(this.props.schema.items as SphereJSONSchemaObject, itemState) as any);
                const itemFormGroup = this.getControlGroup(itemState);
                this.formArray.push(itemFormGroup);
            }
        } else {
            const field = this.fieldsForGroup(this.props.schema.items as SphereJSONSchemaObject);
            this.fields = field ? [field as any] : [];
            const firstItem = this.getControlGroup();
            this.formArray.push(firstItem);
        }
    }

    private getControlGroup(state?: any): UntypedFormGroup {
        const schema = this.props.schema;
        const controls = {};
        for (const [key, itemSchema] of Object.entries((schema.items as SphereJSONSchemaObject).properties)) {
            const validators = [];
            let value: any;
            if (state && Object.prototype.hasOwnProperty.call(state, key)) {
                value = state[key];
            } else if (Object.prototype.hasOwnProperty.call(itemSchema, 'default')) {
                value = (itemSchema as any)?.default;
            }
            (controls as any)[key] = new UntypedFormControl(value);
        }
        return new UntypedFormGroup(controls);
    }

    public fieldsForGroup(itemSchema: SphereJSONSchemaObject, state?: any) {
        const fields = [];
        for (const [key, schema] of Object.entries(itemSchema.properties)) {
            const field = this.formlyBuilder.buildControl(key, itemSchema?.required || [], schema, state?.[key]);
            fields.push(field);
        }
        return fields;
    }

    private initTableColumns(schema: SphereJSONSchemaArray): ColDef[] {
        const cols: ColDef[] = [
            // {
            //     colId: 'index',
            //     headerName: 'Index',
            //     editable: false,
            // }
        ];
        const items = schema.items as SphereJSONSchemaObject;
        for (const [key, itemSchema] of Object.entries(items.properties)) {
            const { cellEditor, cellEditorParams } = this.editorForType(itemSchema);
            cols.push({
                colId: key,
                headerName: (itemSchema as any)?.label || key,
                field: key,
                editable: true,
                cellEditor,
                cellEditorParams,
                valueFormatter: ({ value }) => {
                    if (!value && value !== 0) {
                        return 'Double click to add a value';
                    }
                    return value;
                },
                cellClass: ({ value }) => {
                    if (!value && value !== 0) {
                        return ['text-subtle', 'text-italic'];
                    }
                    return [];
                }
            });
        }
        cols.push({
            colId: 'tools',
            headerName: '',
            editable: false,
            cellRenderer: FormArrayTableToolsCellRendererComponent,
            cellRendererParams: <FormArrayTableToolsParams>{
                onAddRow: row => {
                    if (this.isValidRow(row)) {
                        this.grid()?.api.applyTransaction({ add: [row] });
                        this.pinnedTopRowData = [this.pinnedRow];
                    } else {
                        this.snackbar.open(`You must set values for all required columns. (${this.requiredProps.join(', ')})`, undefined, {
                            duration: 10000
                        });
                    }
                },
                onRemoveRow: row => {
                    this.grid()?.api.applyTransaction({ remove: [row] });
                },
                requiredProperties: this.requiredProps
            }
        });
        return cols;
    }

    private editorForType(itemSchema: SphereJSONSchema): { cellEditor: any; cellEditorParams?: any } {
        if (itemSchema.enum && Array.isArray(itemSchema.enum)) {
            return { cellEditor: 'agRichSelectCellEditor', cellEditorParams: { values: itemSchema.enum } };
        }
        switch (itemSchema.type) {
            case 'number':
            case 'integer': {
                const cellEditorParams: Partial<INumberCellEditorParams> = {};
                if (typeof itemSchema.minimum === 'number') {
                    cellEditorParams.min = itemSchema.minimum;
                }
                if (typeof itemSchema.maximum === 'number') {
                    cellEditorParams.max = itemSchema.maximum;
                }
                if (itemSchema.type === 'integer') {
                    cellEditorParams.step = 1;
                }
                return { cellEditor: NumberCellEditorComponent, cellEditorParams };
            }
            case 'string': {
                if (itemSchema.edmType === 'date') {
                    const cellEditorParams: Partial<IDateStringCellEditorParams> = {};
                    // if (typeof itemSchema.minimum === 'number') {
                    //     cellEditorParams.min = itemSchema.minimum;
                    // }
                    // if (typeof itemSchema.maximum === 'number') {
                    //     cellEditorParams.min = itemSchema.maximum;
                    // }
                    if (typeof itemSchema.format === 'string') {
                        cellEditorParams.format = itemSchema.format;
                    }
                    return { cellEditor: DateStringCellEditorComponent };
                }
            }
        }
        const cellEditorParams: any = {};
        if ((itemSchema as SphereJSONSchemaString).maxLength) {
            cellEditorParams.maxLength = (itemSchema as SphereJSONSchemaString).maxLength;
        }
        return { cellEditor: 'agTextCellEditor', cellEditorParams };
    }

    public onRowsUpdated(rowDataUpdated: RowDataUpdatedEvent) {
        const rows: any[] = [];
        rowDataUpdated.api.forEachNode((node, index) => {
            const { tools, ...properties } = node.data;
            rows.push({ ...properties });
        });
        this.formControl.setValue(rows);
    }

    public onCellValueChanged(event: any) {
        const row = event?.data;
        if (this.isValidRow(row)) {
            this.grid()?.api.applyTransaction({ update: [row] });
        }
    }

    private get pinnedRow() {
        const pinnedRow: any = {};
        for (const column of this.columnDefs.filter(c => c.colId !== 'tools')) {
            pinnedRow[column.colId as string] = undefined;
        }
        return pinnedRow;
    }

    private isValidRow(row: any): boolean {
        const required = this.requiredProps;
        const valid = true;
        for (const property of required) {
            if (
                !Object.prototype.hasOwnProperty.call(row, property) ||
                row[property] === null ||
                row[property] === undefined ||
                row[property] === ''
            ) {
                return false;
            }
        }
        return true;
    }

    private get requiredProps(): readonly string[] {
        try {
            return (this.props.schema.items as SphereJSONSchemaObject).required || [];
        } catch (e) {
            return [];
        }
    }
}
